import React from 'react';
import { safeHasProperty } from '../utils/animationUtils';
import { carryPavilionPage } from '../design/pageAhead';

// Props that any component using this HOC might expect
interface AnimatedComponentProps {
  style?: React.CSSProperties;
  animation?: string | Record<string, any>;
  transition?: string | Record<string, any>;
  [key: string]: any;
}

/**
 * Higher-order component that safely applies animation properties
 * to prevent "Cannot use 'in' operator to search for 'animation' in undefined" errors.
 * It carries the mark withPavilion gives a page of the new design (D-92 part 3:
 * src/design/pageAhead.ts), so a guard's loading screen in front of it still knows the page.
 */
export const withSafeAnimation = <P extends AnimatedComponentProps>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const Safe = (props: P) => {
    // Create a safe copy of the props
    const safeProps = { ...props };
    
    // Handle animation prop safely
    if (safeHasProperty(props, 'animation') && (props as any).animation !== undefined) {
      // If animation is already a string, it's safe to use directly
      if (typeof (props as any).animation === 'string') {
        // Keep it as is
      } 
      // If it's an object, ensure it's safe to use 'in' operator
      else if ((props as any).animation && typeof (props as any).animation === 'object') {
        // It's already an object, so it's safe
      } 
      // Otherwise, remove it to prevent errors
      else {
        delete safeProps.animation;
      }
    }
    
    // Handle transition prop safely
    if (safeHasProperty(props, 'transition') && (props as any).transition !== undefined) {
      // If transition is already a string, it's safe to use directly
      if (typeof (props as any).transition === 'string') {
        // Keep it as is
      } 
      // If it's an object, ensure it's safe to use 'in' operator
      else if ((props as any).transition && typeof (props as any).transition === 'object') {
        // It's already an object, so it's safe
      } 
      // Otherwise, remove it to prevent errors
      else {
        delete safeProps.transition;
      }
    }
    
    // Safely handle style properties related to animations
    if (safeHasProperty(props, 'style') && (props as any).style) {
      const safeStyle: React.CSSProperties = { ...(props as any).style };
      
      // Handle animation property safely in style object
      if (safeHasProperty(safeStyle, 'animation') && 
          typeof safeStyle.animation !== 'string' && 
          !safeStyle.animation) {
        delete safeStyle.animation;
      }
      
      // Handle transition property safely in style object
      if (safeHasProperty(safeStyle, 'transition') && 
          typeof safeStyle.transition !== 'string' && 
          !safeStyle.transition) {
        delete safeStyle.transition;
      }
      
      safeProps.style = safeStyle;
    }
    
    return <Component {...safeProps as P} />;
  };
  carryPavilionPage(Component, Safe);
  return Safe;
};

/**
 * A component wrapper that safely applies animation properties
 */
export const SafeAnimationWrapper: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}> = ({ children, style, className }) => {
  // Create safe style object
  const safeStyle: React.CSSProperties = { ...style };
  
  // Handle animation property safely
  if (safeHasProperty(safeStyle, 'animation') && 
      typeof safeStyle.animation !== 'string' && 
      !safeStyle.animation) {
    delete safeStyle.animation;
  }
  
  // Handle transition property safely
  if (safeHasProperty(safeStyle, 'transition') && 
      typeof safeStyle.transition !== 'string' && 
      !safeStyle.transition) {
    delete safeStyle.transition;
  }
  
  return (
    <div style={safeStyle} className={className}>
      {children}
    </div>
  );
}; 